import { Box, Typography } from '@mui/material';
import { AllowedCurrencyCode } from '@nrdy-marketing-engine/cart/@types/cart-state';
import { ComponentProps } from 'react';

interface ItemPriceProps extends ComponentProps<typeof Box> {
  currencyCode?: AllowedCurrencyCode;
  duration: number | string;
  originalPrice?: number;
  priceCents: number;
  sameFontSize?: boolean;
}

function currencySymbol(currencyCode: AllowedCurrencyCode) {
  return { GBP: '£', USD: '$', CAD: 'CA$' }[currencyCode];
}

function centsToPriceConverter(priceCents: number) {
  // if it's a whole number, return it as is
  if (priceCents % 100 === 0) return priceCents / 100;

  return (priceCents / 100.0).toFixed(2);
}

function UniSizedItemPrice({
  priceCents,
  duration,
  sameFontSize,
  currencyCode,
  originalPrice,
  ...boxProps
}: ItemPriceProps) {
  return (
    <Box alignItems="end" display="flex" marginBottom=".5rem" width="100%" {...boxProps}>
      <Typography
        component="span"
        data-testid="price"
        fontSize="24px"
        fontWeight="bold"
        lineHeight={1.125}
      >
        {`${currencySymbol(currencyCode)}${centsToPriceConverter(priceCents)} ${duration}`}
      </Typography>
      {originalPrice && originalPrice !== priceCents && (
        <Typography color="red" mx={1} sx={{ textDecorationLine: 'line-through' }} variant="body2">
          {currencySymbol(currencyCode)}
          {centsToPriceConverter(originalPrice)}
        </Typography>
      )}
    </Box>
  );
}

function ItemPrice(props: ItemPriceProps) {
  const { priceCents, duration, sameFontSize, currencyCode, originalPrice, ...boxProps } = props;

  if (sameFontSize) return <UniSizedItemPrice {...props} />;

  return (
    <Box alignItems="end" display="flex" width="100%" {...boxProps}>
      <Typography component="span" fontSize="24px" fontWeight="bold" lineHeight={1.125}>
        {currencySymbol(currencyCode)}
        {centsToPriceConverter(priceCents)}
      </Typography>
      {originalPrice && originalPrice !== priceCents && (
        <Typography color="red" mx={1} sx={{ textDecorationLine: 'line-through' }} variant="body2">
          {currencySymbol(currencyCode)}
          {centsToPriceConverter(originalPrice)}
        </Typography>
      )}
      <Typography component="span" fontSize="14px" marginLeft="4px" marginRight="4px">
        /
      </Typography>
      <Typography component="span" fontSize="14px">
        {duration}
      </Typography>
    </Box>
  );
}

export default ItemPrice;
